/**
 * MobileAnalyticsTab.jsx
 * Training analytics for the mobile NutritionScreen
 * Shows stats, training frequency, and calorie trends
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Flame, TrendingUp, Calendar, Award, Loader2, BarChart3 } from 'lucide-react';
import { formatStat } from '../../../../utils/format';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MobileAnalyticsTab = ({ portalToken }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    loadAnalytics();
  }, [portalToken, period]);

  const loadAnalytics = async () => {
    if (!portalToken) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/nutrition/training-log/analytics`, {
        params: { days: parseInt(period) },
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-500 text-sm">{t('common.no_data')}</p>
      </div>
    );
  }

  const summary = analytics.summary || {};
  const weeklyData = analytics.weekly_data || [];
  const typeDistribution = analytics.type_distribution || [];
  const maxWeeklyCalories = Math.max(...weeklyData.map(w => w.total_calories || 0), 1);

  return (
    <div className="p-4 space-y-4 pb-32" data-testid="mobile-analytics-tab">
      {/* Period Selector */}
      <div className="flex gap-2">
        {[
          { value: '7', label: t('analytics.period_7') },
          { value: '30', label: t('analytics.period_30') },
          { value: '90', label: t('analytics.period_90') },
        ].map(p => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={`flex-1 py-2 rounded-xl text-xs font-medium transition-colors ${
              period === p.value
                ? 'bg-yellow-500 text-black'
                : 'bg-zinc-800 text-zinc-400'
            }`}
            data-testid={`analytics-period-${p.value}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-2xl p-3 border border-orange-500/30">
          <div className="flex items-center gap-2 mb-1">
            <Flame className="w-4 h-4 text-orange-400" />
            <span className="text-zinc-400 text-xs">{t('analytics.calories')}</span>
          </div>
          <p className="text-white text-xl font-bold">{Math.round(summary.total_calories || 0)}</p>
          <p className="text-zinc-500 text-[10px]">{t('analytics.burned')}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-2xl p-3 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-blue-400" />
            <span className="text-zinc-400 text-xs">{t('analytics.sessions')}</span>
          </div>
          <p className="text-white text-xl font-bold">{formatStat(summary.total_sessions)}</p>
          <p className="text-zinc-500 text-[10px]">{t('analytics.units')}</p>
        </div>

        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-2xl p-3 border border-green-500/30">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-zinc-400 text-xs">{t('analytics.average')}</span>
          </div>
          <p className="text-white text-xl font-bold">{formatStat(Math.round(summary.avg_calories_per_session || 0))}</p>
          <p className="text-zinc-500 text-[10px]">{t('analytics.per_session')}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-2xl p-3 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-4 h-4 text-purple-400" />
            <span className="text-zinc-400 text-xs">{t('analytics.streaks')}</span>
          </div>
          <p className="text-white text-xl font-bold">{formatStat(summary.current_streak)}</p>
          <p className="text-zinc-500 text-[10px]">{t('analytics.consecutive_days')}</p>
        </div>
      </div>

      {/* Weekly Bar Chart */}
      {weeklyData.length > 0 && (
        <div className="bg-zinc-800/50 rounded-2xl p-4 border border-zinc-700/50">
          <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider mb-3">{t('analytics.weekly_trend')}</p>
          <div className="flex items-end gap-1 h-32">
            {weeklyData.slice(-8).map((week, idx) => {
              const height = maxWeeklyCalories > 0
                ? Math.max(4, (week.total_calories / maxWeeklyCalories) * 100)
                : 4;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] text-zinc-500">{Math.round(week.total_calories || 0)}</span>
                  <div
                    className="w-full bg-gradient-to-t from-orange-500 to-yellow-500 rounded-t-md transition-all"
                    style={{ height: `${height}%`, minHeight: '4px' }}
                  />
                  <span className="text-[8px] text-zinc-600">KW{week.week_number || idx + 1}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Type Distribution */}
      {typeDistribution.length > 0 && (
        <div className="bg-zinc-800/50 rounded-2xl p-4 border border-zinc-700/50">
          <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider mb-3">{t('analytics.training_types')}</p>
          <div className="space-y-2">
            {typeDistribution.map((type, idx) => {
              const maxCount = Math.max(...typeDistribution.map(t => t.count || 0), 1);
              const width = ((type.count || 0) / maxCount) * 100;
              const colors = ['bg-orange-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-teal-500'];
              return (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-white text-xs w-24 truncate">{type.activity_type || type.name}</span>
                  <div className="flex-1 h-5 bg-zinc-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${colors[idx % colors.length]} rounded-full transition-all`}
                      style={{ width: `${width}%` }}
                    />
                  </div>
                  <span className="text-zinc-400 text-xs w-8 text-right">{type.count}x</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileAnalyticsTab;
